import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { messageOf } from '../../shared/queries.ts'
import { changeOwnPasswordApi } from './password-api.ts'

/**
 * ══ FORM TỰ ĐẶT / ĐỔI MẬT KHẨU — DÙNG CHUNG ba chỗ (E13 sửa 19/10/2026) ══════════════════════════════════════════
 * Thẻ 🔒 Mật khẩu ở Cài đặt (bản cũ `PasswordCard.tsx` ①) · hộp 🔒 ở đầu trang (cho vai không vào được `/settings`) ·
 * màn ÉP đổi mật khẩu tạm. Một form, một bộ câu chữ — không ba bản sao lệch nhau.
 *
 * ⚠ Màn KHÔNG kiểm luật mật khẩu (4 điều kiện): máy chủ (`passwordRuleError`) trả câu đầy đủ — kiểm lại ở đây là dựng bản
 *   sao thứ hai của luật. Phép kiểm DUY NHẤT ở màn là hai ô nhập lại phải khớp (máy chủ chỉ nhận một ô).
 * ⚠ Ô "Mật khẩu hiện tại" CHỈ hiện khi tài khoản ĐÃ có mật khẩu (bản cũ `PasswordCard.tsx:113`): người mới chỉ có FADO SSO
 *   thì đặt thẳng.
 */
export interface ChangeOwnPasswordFormProps {
  passSet: boolean
  /** Gọi sau khi máy chủ nhận — nơi dùng làm mới trạng thái / đóng hộp / vào ứng dụng. */
  onDone: () => void
}

const inCls = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
const lblCls = 'mb-1 block text-xs font-semibold text-slate-500'

export const ChangeOwnPasswordForm = ({ passSet, onDone }: ChangeOwnPasswordFormProps): JSX.Element => {
  const { t } = useTranslation()
  const [oldPassword, setOld] = useState('')
  const [newPassword, setNew] = useState('')
  const [confirm, setConfirm] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (busy) return
    setErr(null)
    if (newPassword !== confirm) { setErr(t('login.nccPw.mismatch')); return }
    setBusy(true)
    try {
      await changeOwnPasswordApi(passSet ? { oldPassword, newPassword } : { newPassword })
      setOld(''); setNew(''); setConfirm('')
      onDone()
    } catch (x) {
      setErr(messageOf(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3" data-testid="form-doi-mat-khau">
      {passSet && (
        <div>
          <label htmlFor="pw-old" className={lblCls}>{t('login.nccPw.oldLabel')}</label>
          <input id="pw-old" type="password" value={oldPassword} required className={inCls}
                 autoComplete="current-password" onChange={(e) => { setOld(e.target.value) }} />
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="pw-new" className={lblCls}>{t('login.nccPw.newLabel')}</label>
          <input id="pw-new" type="password" value={newPassword} required className={inCls}
                 autoComplete="new-password" onChange={(e) => { setNew(e.target.value) }} />
        </div>
        <div>
          <label htmlFor="pw-new2" className={lblCls}>{t('login.nccPw.confirmLabel')}</label>
          <input id="pw-new2" type="password" value={confirm} required className={inCls}
                 autoComplete="new-password" onChange={(e) => { setConfirm(e.target.value) }} />
        </div>
      </div>
      <p className="text-[11px] text-slate-500">{t('login.pw.ruleHint')}</p>
      {err !== null && (
        <p role="alert" className="whitespace-pre-line rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</p>
      )}
      <button type="submit" disabled={busy}
              className="rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2 text-sm font-bold text-white hover:from-brand-600 hover:to-brand-700 disabled:opacity-40">
        {busy ? t('login.nccPw.submitting') : passSet ? t('login.nccPw.submit') : t('login.pw.setSubmit')}
      </button>
    </form>
  )
}
